const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * <mellow-carousel>: a scroll-snap track (native swiping) with previous/next buttons,
 * indicators, keyboard support and an optional, pausable autoplay (WCAG 2.2.2).
 */
export class MellowCarousel extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.track?.addEventListener('scroll', () => {
            cancelAnimationFrame(this._frame);
            this._frame = requestAnimationFrame(() => this.update());
        }, { passive: true });

        this.addEventListener('click', (event) => {
            if (event.target.closest('[data-carousel-prev]')) this.go(this.index - 1, true);
            else if (event.target.closest('[data-carousel-next]')) this.go(this.index + 1, true);
            else if (event.target.closest('[data-carousel-play]')) this.togglePlay();
            else {
                const dot = event.target.closest('[data-carousel-go]');
                if (dot) this.go(Number(dot.dataset.carouselGo), true);
            }
        });

        this.addEventListener('keydown', (event) => {
            if (event.target.closest('input, textarea, select, [contenteditable]')) return;
            const rtl = getComputedStyle(this).direction === 'rtl';
            if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) {
                event.preventDefault();
                this.go(this.index + 1, true);
            } else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) {
                event.preventDefault();
                this.go(this.index - 1, true);
            }
        });

        // Autoplay pauses while the user interacts (hover or keyboard focus).
        this.addEventListener('pointerenter', () => (this._hovered = true));
        this.addEventListener('pointerleave', () => (this._hovered = false));
        this.addEventListener('focusin', () => (this._focused = true));
        this.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget)) this._focused = false;
        });

        this.playing = this.hasAttribute('autoplay') && !reducedMotion.matches;
        this.sync();
        this.startTimer();
    }

    disconnectedCallback() {
        clearInterval(this._timer);
    }

    get track() {
        return this.querySelector('.mellow-carousel-track');
    }

    get items() {
        return [...this.querySelectorAll('.mellow-carousel-item')];
    }

    get perView() {
        return Math.max(1, Number(getComputedStyle(this).getPropertyValue('--_per-view')) || 1);
    }

    /** Pages: with several slides per view we stop at the last full page. */
    get lastIndex() {
        return Math.max(0, this.items.length - Math.floor(this.perView));
    }

    /** Label the slides ("2 of 5") and build the indicators – also after Livewire updates. */
    sync() {
        const items = this.items;
        const label = this.dataset.slideLabel ?? ':index / :count';
        items.forEach((item, i) => item.setAttribute('aria-label', label.replace(':index', i + 1).replace(':count', items.length)));

        const indicators = this.querySelector('.mellow-carousel-indicators');
        if (indicators && indicators.children.length !== this.lastIndex + 1) {
            const go = indicators.dataset.goLabel ?? ':index';
            indicators.replaceChildren(...Array.from({ length: this.lastIndex + 1 }, (_, i) => {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = 'mellow-carousel-dot';
                dot.dataset.carouselGo = i;
                dot.setAttribute('aria-label', go.replace(':index', i + 1));
                return dot;
            }));
        }

        this.update();
    }

    update() {
        const track = this.track;
        const items = this.items;
        if (!track || !items.length) return;

        // Closest slide to the start edge of the track.
        const start = track.getBoundingClientRect().left;
        const rtl = getComputedStyle(this).direction === 'rtl';
        let index = 0;
        let best = Infinity;
        items.forEach((item, i) => {
            const rect = item.getBoundingClientRect();
            const distance = Math.abs((rtl ? track.getBoundingClientRect().right - rect.right : rect.left - start));
            if (distance < best) {
                best = distance;
                index = i;
            }
        });

        this.index = Math.min(index, this.lastIndex);
        const loop = this.hasAttribute('loop');

        this.querySelector('[data-carousel-prev]')?.toggleAttribute('disabled', !loop && this.index === 0);
        this.querySelector('[data-carousel-next]')?.toggleAttribute('disabled', !loop && this.index >= this.lastIndex);
        this.querySelectorAll('[data-carousel-go]').forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));

        // Slides outside the view are hidden from assistive tech and the tab order.
        items.forEach((item, i) => {
            const visible = i >= this.index && i < this.index + this.perView;
            item.inert = !visible;
        });
    }

    go(index, user = false) {
        const last = this.lastIndex;
        if (this.hasAttribute('loop')) index = (index + last + 1) % (last + 1);
        index = Math.max(0, Math.min(index, last));

        const item = this.items[index];
        if (!item) return;

        this.track.scrollTo({
            left: item.offsetLeft - this.track.offsetLeft,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
        });

        // A user choosing a slide stops the automatic slide show.
        if (user && this.playing) this.togglePlay(false);
    }

    togglePlay(force = !this.playing) {
        this.playing = force;
        const button = this.querySelector('[data-carousel-play]');
        if (button) {
            button.toggleAttribute('data-paused', !this.playing);
            button.setAttribute('aria-label', this.playing ? this.dataset.pauseLabel : this.dataset.playLabel);
        }
        this.track?.setAttribute('aria-live', this.playing ? 'off' : 'polite');
        this.startTimer();
    }

    startTimer() {
        clearInterval(this._timer);
        if (!this.playing) return;

        this._timer = setInterval(() => {
            if (this._hovered || this._focused || document.hidden) return;
            const next = this.index >= this.lastIndex ? 0 : this.index + 1;
            this.go(next);
        }, Number(this.getAttribute('autoplay')) || 5000);
    }
}
